import React, { useContext } from 'react';

import { makeMuiStyles, useCommonStyles } from '@styles';
import clsx from 'clsx';

const useStyles = makeMuiStyles((theme, _common) => ({
  button: {
    background: theme.palette.grey[100],
    height: 45,
    padding: '0.6rem',
    flex: 1,
    borderBottom: '1px solid transparent',
  },
  activeButton: {
    color: theme.palette.primary.main,
    borderBottomColor: theme.palette.primary.main,
  },
}));

const stateContext = React.createContext<StateContextValues>(
  new Proxy({} as unknown as StateContextValues, {
    get: () => {
      throw new Error('');
    },
  }),
);

const TabButton: React.FC<{ htmlFor: string; children: React.ReactNode }> = (props) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };
  const { children, htmlFor } = props;
  const { state, activate } = useContext(stateContext);

  const isActive = state.activeId === htmlFor;

  return (
    <button
      className={clsx(classes.button, isActive && classes.activeButton)}
      onClick={() => {
        activate(htmlFor);
      }}
    >
      {children}
    </button>
  );
};

const TabPanel: React.FC<React.PropsWithChildren<{ id: string }>> = (props) => {
  const { children, id } = props;
  const { state } = useContext(stateContext);

  return state.activeId === id ? <>{children}</> : null;
};

export const Tab: React.FC<React.PropsWithChildren<TabProps>> & Aggregation = (props) => {
  const { children, activeId, onChangeActiveId } = props;

  return (
    <stateContext.Provider
      value={{
        state: {
          activeId,
        },
        activate: onChangeActiveId,
      }}
    >
      {children}
    </stateContext.Provider>
  );
};

Tab.Button = TabButton;
Tab.Panel = TabPanel;

export interface TabProps {
  activeId: string;
  onChangeActiveId: (id: string) => void;
}

interface Aggregation {
  Button: typeof TabButton;
  Panel: typeof TabPanel;
}

interface TabState {
  activeId: string;
}

interface StateContextValues {
  state: TabState;
  activate(id: string): any;
}
